import { AssistentEntityForm } from './AssistentEntityForm';
import { AssistentItem } from './assistent_config';
import { ContextService } from '../../../services/context_service';
import { IDs } from '../../../helpers/IDs';
import { lazyInject } from '../../../services/inversify';
import * as React from 'react';
import { transformWerteKennziffern } from '../functions/dataHandlingForKennziffern';

interface AssistentPrueferProps extends React.ClassAttributes<any> {
    data: any;
    standortSelected: any;
    jahr: number;
    items: AssistentItem[];
    pruefState: any; // Eingehender Prüfstate
    onPruefStateChange(pruefState: any): void;
}

interface AssistentPrueferState {
    isChecking: boolean;
    runIndex: number; // Index des aktuellen Items aus der Config
    pruefState: any; // Lokaler pruefstate
}

export class AssistentPruefer extends React.Component<AssistentPrueferProps, AssistentPrueferState> {

    constructor(props: any) {
        super(props);
        this.state = {
            runIndex: 0,
            isChecking: false,
            pruefState: {}
        };
    }

    @lazyInject(ContextService) private contextService: ContextService;

    private config: Array<AssistentItem>;

    render(): JSX.Element {
        // console.log('Pruefer', this.state.pruefState);
        this.check();
        const currentItem = this.props.items[this.state.runIndex];
        return (
            <div style={{ backgroundColor: 'yellow', maxHeight: 0, maxWidth: 0, overflow: 'hidden' }}>
                {this.renderCheckItem()}
            </div>
        );
    }

    check() {
        // Prüfen, ob wir starten müssen
        if (!this.props.pruefState && !this.state.isChecking &&                   // Kein Prüfstate, und wir checken noch nicht
            this.props.jahr && this.props.standortSelected && this.props.items) { // ... und alle benötigten Daten sind da
            this.setState({ isChecking: true, pruefState: {}, runIndex: 0 });
            // console.log('AssistentPruefer: Start checking ...');
            return;
        }
        // Prüfen, ob wir prüfen und für das aktuelle Item einen Prüfstate haben ...
        if (this.state.isChecking) {
            const currentItem = this.props.items[this.state.runIndex];
            // Prüfen, ob wir am Ende sind
            if (!currentItem) {
                // console.log('AssistentPruefer: No Items left!');
                // Wir sind am Ende!
                // In jedem Fall etwas in den pruefState Schreiben!
                const prfState = this.state.pruefState;
                prfState.done = true;
                this.setState({ isChecking: false, pruefState: prfState });
                // console.log('FERTIG', prfState);
                this.props.onPruefStateChange(prfState);
                return;
            }
            // Prüfen, ob uns das Item ggf. nicht interessiert
            if (currentItem.type !== 'EntityForm') {
                this.nextItem();
                return;
            }
            if (!this.state.pruefState[currentItem.entity]) {
                // Wenn wir hier ankommen, haben wir eine neue Entity!
                const daten = this.findData(currentItem.entity);
                // console.log('AssistentPruefer: Data for new Entity: ', currentItem.entity, daten);
                let datenFiltered = [];
                if (currentItem.entity === IDs.ENT_UNTERNEHMENSDATEN) {
                    datenFiltered = [...daten];
                } else if (currentItem.entity === IDs.ENT_STANDORT) {
                    datenFiltered = [...daten];
                } else {
                    datenFiltered = daten.filter((item: any) => (!item.monat || item.monate === 0)
                        && (item.standort_tid_orig > 0 || item.standort_tid > 0) && !item.parent_tid);
                }
                const prfItem: any = {
                    anzahl: datenFiltered.length,
                    datenUngeprueft: datenFiltered,
                    pruefResult: [],
                    pruefTids: []
                };
                // console.log(`AssistentPruefer: Setting new Prüf-Item [${currentItem.entity}]`, prfItem);
                const prfState = this.state.pruefState;
                prfState[currentItem.entity] = prfItem;
                this.setState({ pruefState: prfState });
                return;
            }
            // OK ... es sollte ein pruefItem geben!
            const pruefState = this.state.pruefState;
            const pruefItem = pruefState[currentItem.entity];
            if (pruefItem) {
                // Wenn keine Daten mehr zu prüfen sind oder die Anzahl sowieso 0 ist, dann weiter
                if (pruefItem.datenUngeprueft.length === 0 || pruefItem.anzahl === 0) {
                    // Item ist erledigt ... auf zum nächsten Item
                    this.nextItem();
                }
            }
            // console.log('AssistentPruefer: Keep on checking .... NOTHING TO DO');
        }
    }

    nextItem() {
        const runIndex = this.state.runIndex + 1;
        // console.log(`AssistentPruefer: Next Item: ${this.state.runIndex} -> ${runIndex} von ${this.props.items.length}`);
        if (runIndex <= this.props.items.length) {
            window.setTimeout(() => this.setState({ runIndex }), 10);
        }
    }

    findData(entity: string) {
        let daten = this.props.data[entity] || [];
        if (entity === IDs.ENT_KENNZIFFERN_GENERATED) {
            const werte = this.props.data[IDs.ENT_KENNZAHLEN_WERTE];
            daten = transformWerteKennziffern(werte);
        }
        const parentEntities: Array<string> = [IDs.ENT_CO2_SCOPE3_PENDELN, IDs.ENT_CO2_SCOPE3_REISEN];
        const datenFiltered = daten.filter((ds: any) =>
            (!ds.monat || ds.monat === 0) && (!ds.jahr || ds.jahr === this.props.jahr)
            && (!ds.standort_tid_orig || ds.standort_tid_orig === this.props.standortSelected.tid)
            && (parentEntities.indexOf(entity) === -1 || (+ds.parent_tid || 0) === 0)
        );
        return datenFiltered;
    }

    setValidationState(datensatz: any, state: any) {
        const currentItem = this.props.items[this.state.runIndex];
        const entity = currentItem.entity;
        const pruefState = this.state.pruefState;
        const pruefItem = this.state.pruefState[entity];
        // Invarianz ... wieviele Results fehlen uns noch und wieviele haben wir
        // if (entity === IDs.ENT_ENERGIEDATEN_STROM) {
        //      console.log('setValidation 1', pruefItem, [...pruefItem.datenUngeprueft], datensatz.tid);
        // }
        if (
            pruefItem.anzahl === (pruefItem.pruefResult.length + pruefItem.datenUngeprueft.length)
            && pruefItem.datenUngeprueft.length > 0
        ) {
            // OK, Anzahl passt --> wollen nur sicher gehen hire nicht den State doppelt zu setzen
            if (!pruefItem.pruefTids.some((id: number) => id === datensatz.tid)) {
                pruefItem.pruefResult.push(state);
                pruefItem.pruefTids.push(datensatz.tid);
                pruefItem.datenUngeprueft.splice(0, 1);
                pruefState[currentItem.entity] = pruefItem;
                if (currentItem.entity === IDs.ENT_ENERGIEDATEN_WAERME) {
                    // console.log('AssistentPruefer: Setting Prüfstate: ', pruefItem);
                }
            }
            window.setTimeout(() => this.setState({ pruefState }), 100);
        }
        // if (entity === IDs.ENT_ENERGIEDATEN_STROM) {
        //     console.log('setValidation 2', pruefItem, [...pruefItem.datenUngeprueft], datensatz.tid);
        // }
    }

    renderCheckItem() {
        const currentItem = this.props.items[this.state.runIndex];
        if (currentItem && currentItem.type === 'EntityForm') {
            const entity = currentItem.entity;
            // console.log('Prüfe: ', entity);
            const pruefItem = this.state.pruefState[entity];
            if (entity === IDs.ENT_ENERGIEDATEN_WASSER) {
                // console.log('AssistentPruefer: Prf:', pruefItem);
            }
            if (pruefItem) {
                const datenUngeprueft = pruefItem.datenUngeprueft;
                const datensatz = datenUngeprueft[0];
                if (datenUngeprueft && datenUngeprueft.length > 0) {
                    if (entity === IDs.ENT_ENERGIEDATEN_WASSER) {
                        // console.log('AssistentPruefer: Form: ', datensatz, datenUngeprueft);
                    }
                    return <AssistentEntityForm
                        key={`aef_${this.state.runIndex}`}
                        item={currentItem}
                        data={this.props.data}
                        dataItem={datensatz}
                        jahr={this.props.jahr}
                        isPruefung={true}
                        standort={this.props.standortSelected}
                        onValidationStateChange={(state: any) => {
                            if (entity === IDs.ENT_ENERGIEDATEN_WAERME) {
                                // console.log('VALIDATION: ', entity, datensatz, state);
                            }
                            if (currentItem.entity === IDs.ENT_ENERGIEDATEN_STROM) {
                                // console.log('CHECKED: ', datensatz, state);
                            }
                            this.setValidationState(datensatz, state);
                        }}
                    />;
                }
            }
        }
        return 'NoRender';
    }
}
